import { useState, useEffect, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import PublicShareCardCTAs from "./PublicShareCardCTAs";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import PhygitalIcon, { PoweredByPhygital, PHYGITAL_AVATAR_BLACK } from "@/components/PhygitalIcon";
import { toast } from "sonner";
import {
  Scissors,
  Sparkles,
  Palette,
  Eye,
  Calendar,
  AlertCircle,
  Check,
  Copy,
  Download,
  MessageSquare,
  Heart,
  Camera,
  ExternalLink,
  ArrowRight,
  User,
  Utensils,
  Accessibility,
  MapPin,
  Clock,
  Share,
  Link2
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Category configurations
const CATEGORY_CONFIG = {
  hair: { icon: Scissors, color: "from-[#e83e8c] to-pink-500", bgColor: "bg-[#ffeef4]", textColor: "text-[#e83e8c]", borderColor: "border-[#ffb8d0]", name: "Hair" },
  nails: { icon: Sparkles, color: "from-[#f15a70] to-red-400", bgColor: "bg-[#ffe8ec]", textColor: "text-[#f15a70]", borderColor: "border-[#ffa8b5]", name: "Nails" },
  makeup: { icon: Sparkles, color: "from-[#9c57f5] to-purple-500", bgColor: "bg-[#f2e9ff]", textColor: "text-[#9c57f5]", borderColor: "border-[#d4bbff]", name: "Makeup" },
  face: { icon: Palette, color: "from-[#9c57f5] to-purple-500", bgColor: "bg-[#f2e9ff]", textColor: "text-[#9c57f5]", borderColor: "border-[#d4bbff]", name: "Face/Makeup" },
  lash: { icon: Eye, color: "from-[#774cda] to-violet-500", bgColor: "bg-[#ede7f7]", textColor: "text-[#774cda]", borderColor: "border-[#c0aaee]", name: "Lashes" },
  lashes: { icon: Eye, color: "from-[#774cda] to-violet-500", bgColor: "bg-[#ede7f7]", textColor: "text-[#774cda]", borderColor: "border-[#c0aaee]", name: "Lashes" },
  event: { icon: Calendar, color: "from-[#f9c434] to-yellow-500", bgColor: "bg-[#fff5e6]", textColor: "text-[#f9c434]", borderColor: "border-[#ffe08a]", name: "Event" },
  culinary: { icon: Utensils, color: "from-[#f28c28] to-orange-500", bgColor: "bg-[#fff3e6]", textColor: "text-[#f28c28]", borderColor: "border-[#ffc480]", name: "Culinary" },
  custom: { icon: Sparkles, color: "from-gray-500 to-gray-600", bgColor: "bg-gray-50", textColor: "text-gray-700", borderColor: "border-gray-200", name: "Experience" }
};

// Human-readable preference labels
const PREFERENCE_LABELS = {
  // Accessibility
  wheelchair: "Wheelchair Accessible",
  wheelchair_accessible: "Wheelchair Accessible",
  extra_time: "Extra Time Needed",
  extra_time_needed: "Extra Time Needed",
  hearing_assistance: "Hearing Assistance",
  service_animal: "Service Animal",
  fragrance_free: "Fragrance-Free",
  limited_mobility: "Limited Mobility",
  // Dietary
  vegan: "Vegan",
  vegetarian: "Vegetarian",
  gluten_free: "Gluten-Free",
  dairy_free: "Dairy-Free",
  nut_free: "Nut-Free Allergy",
  kosher: "Kosher",
  halal: "Halal",
  pescatarian: "Pescatarian",
  // Skin
  oily: "Oily Skin",
  dry: "Dry Skin",
  combination: "Combination Skin",
  sensitive: "Sensitive Skin",
  normal: "Normal Skin",
  // Generic
  yes: "Yes",
  no: "No"
};

const getPreferenceLabel = (value) => {
  if (!value) return "";
  const key = String(value).toLowerCase().replace(/[\s-]/g, '_');
  return PREFERENCE_LABELS[key] || value.replace?.(/_/g, ' ')?.replace?.(/\b\w/g, l => l.toUpperCase()) || value;
};

/**
 * PublicPreferenceCardPage - Non-platform view for shared preference cards
 * This is what providers see when they receive a shared link
 * Route: /x/:cardId
 */
const PublicPreferenceCardPage = () => {
  const { shareId } = useParams();
  const [card, setCard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [linkCopied, setLinkCopied] = useState(false);
  const cardRef = useRef(null);

  // Track view on mount
  useEffect(() => {
    trackView();
    fetchCard();
  }, [shareId]);

  const trackView = async () => {
    try {
      // Track that this card was viewed (for analytics)
      await fetch(`${API}/x/${shareId}/view`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          viewed_at: new Date().toISOString(),
          referrer: document.referrer || null,
          user_agent: navigator.userAgent
        })
      });
    } catch (err) {
      // Silent fail - tracking is not critical
      console.log("View tracking:", err.message);
    }
  };

  const fetchCard = async () => {
    try {
      const res = await fetch(`${API}/x/${shareId}`);
      if (!res.ok) {
        if (res.status === 404) setError("not_found");
        else setError("unknown");
        return;
      }
      const data = await res.json();
      setCard(data);
    } catch (err) {
      console.error("Error fetching card:", err);
      setError("unknown");
    } finally {
      setLoading(false);
    }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    setLinkCopied(true);
    toast.success("Link copied!");
    setTimeout(() => setLinkCopied(false), 2000);
  };

  const downloadCard = async () => {
    if (!cardRef.current) return;
    try {
      const html2canvas = (await import("html2canvas")).default;
      const canvas = await html2canvas(cardRef.current, { backgroundColor: "#ffffff", scale: 2 });
      const link = document.createElement("a");
      link.href = canvas.toDataURL("image/png");
      link.download = `preference-card-${shareId}.png`;
      link.click();
      toast.success("Card downloaded!");
    } catch (err) {
      toast.error("Could not download card");
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-2 border-gray-200 border-t-gray-900 rounded-full animate-spin" />
          <p className="text-gray-600 text-sm">Loading preference card...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-red-500" />
          </div>
          <h1 className="text-xl font-semibold text-gray-900 mb-2">
            {error === "not_found" ? "Card Not Found" : "Something Went Wrong"}
          </h1>
          <p className="text-gray-600 mb-6">
            {error === "not_found" 
              ? "This preference card doesn't exist or has been removed." 
              : "We couldn't load this preference card. Please try again."
            }
          </p>
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 px-6 py-3 bg-gray-900 text-white rounded-full hover:bg-gray-800 transition-colors"
          >
            <PhygitalIcon className="w-5 h-5" variant="light" />
            Go to Phygital
          </Link>
        </div>
      </div>
    );
  }

  const category = card?.category || "custom";
  const config = CATEGORY_CONFIG[category] || CATEGORY_CONFIG.custom;
  const CategoryIcon = config.icon;
  
  // Compute whether we have accessibility/dietary data to show
  const accessibilityData = card?.accessibility || card?.preferences?.accessibility;
  const hasAccessibility = accessibilityData && Object.values(accessibilityData).some(v => v && v !== false);
  const dietaryData = card?.dietary || card?.preferences?.dietary;
  const hasDietary = dietaryData && Object.values(dietaryData).some(v => v && v !== false);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50" data-testid="public-preference-card-page">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm border-b border-gray-100 sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <PhygitalIcon className="w-6 h-6" />
            <span className="font-semibold text-gray-900">phygital</span>
          </Link>
          <div className="flex items-center gap-2">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={copyLink} 
              className="gap-1.5"
              data-testid="copy-link-btn"
            >
              {linkCopied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
              <span className="hidden sm:inline">{linkCopied ? "Copied!" : "Copy Link"}</span>
            </Button>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={downloadCard} 
              className="gap-1.5"
              data-testid="download-btn"
            >
              <Download className="w-4 h-4" />
              <span className="hidden sm:inline">Save</span>
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-2xl mx-auto px-4 py-8">
        {/* Preference Card */}
        <div 
          ref={cardRef} 
          className="bg-white rounded-3xl shadow-xl border border-gray-100 overflow-hidden mb-6"
          data-testid="preference-card"
        >
          {/* Card Header with Category Background */}
          <div className={`bg-gradient-to-r ${config.color} px-6 py-8 text-white relative overflow-hidden`}>
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -skew-x-12" />
            <div className="relative">
              {/* Category Badge */}
              <div className="flex items-center gap-2 mb-3">
                <CategoryIcon className="w-5 h-5" />
                <span className="text-sm font-medium opacity-90">{config.name} Preferences</span>
              </div>

              {/* User Info */}
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center overflow-hidden border-2 border-white/30">
                  {card?.user_photo ? (
                    <img src={card.user_photo} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <User className="w-8 h-8 text-white/80" />
                  )}
                </div>
                <div>
                  <h2 className="text-2xl font-bold">{card?.user_name || "Anonymous"}</h2>
                  <div className="flex items-center gap-2 mt-1 text-white/80">
                    <Calendar className="w-4 h-4" />
                    <span className="text-sm">
                      {card?.target_date 
                        ? new Date(card.target_date).toLocaleDateString('en-US', { 
                            weekday: 'short', month: 'short', day: 'numeric' 
                          })
                        : 'Date TBD'
                      }
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Card Content */}
          <div className="p-6">
            {/* Inspiration Images */}
            {(card?.inspo_images?.length > 0 || (card?.inspiration_images?.length > 0 && !card?.inspo_images)) && (
              <div className="mb-6">
                <h3 className="text-sm font-semibold text-gray-600 uppercase tracking-wide mb-3 flex items-center gap-2">
                  <Camera className="w-4 h-4" />
                  Inspiration
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {(card.inspo_images || card.inspiration_images).slice(0, 6).map((img, idx) => (
                    <div key={idx} className="aspect-square rounded-xl overflow-hidden bg-gray-100 shadow-md">
                      <img 
                        src={img.image_url || img} 
                        alt={`Inspiration ${idx + 1}`} 
                        className="w-full h-full object-cover hover:scale-105 transition-transform duration-300" 
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Category-Specific Defaults (Hair type/texture, Nail shape, etc.) */}
            {card?.preferences && Object.keys(card.preferences).filter(k => k !== 'dietary' && k !== 'accessibility').length > 0 && (
              <div className="mb-6">
                <h3 className="text-sm font-semibold text-gray-600 uppercase tracking-wide mb-3 flex items-center gap-2">
                  <CategoryIcon className="w-4 h-4" />
                  {config.name} Details
                </h3>
                <div className={`${config.bgColor} rounded-xl p-4 border ${config.borderColor}`}>
                  <div className="grid grid-cols-2 gap-3">
                    {Object.entries(card.preferences)
                      .filter(([key]) => key !== 'dietary' && key !== 'accessibility')
                      .map(([key, value]) => {
                        if (!value) return null;
                        return (
                          <div key={key} data-testid={`pref-${key}`}>
                            <p className="text-xs text-gray-500 capitalize">{key.replace(/_/g, ' ')}</p>
                            <p className={`font-medium text-sm ${config.textColor}`}>
                              {Array.isArray(value) ? value.join(', ') : getPreferenceLabel(value)}
                            </p>
                          </div>
                        );
                      })}
                  </div>
                </div>
              </div>
            )}

            {/* Accessibility Needs */}
            {hasAccessibility && (
              <div className="mb-6">
                <h3 className="text-sm font-semibold text-gray-600 uppercase tracking-wide mb-3 flex items-center gap-2">
                  <Accessibility className="w-4 h-4" />
                  Accessibility
                </h3>
                <div className="bg-teal-50 rounded-xl p-4 border border-teal-200">
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(card?.accessibility || card?.preferences?.accessibility || {}).map(([key, value]) => {
                      if (!value || value === false) return null;
                      return (
                        <Badge key={key} variant="secondary" className="bg-teal-50 text-teal-700">
                          {getPreferenceLabel(typeof value === 'boolean' ? key : value)}
                        </Badge>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            {/* Dietary - only shows for food categories or host-requested */}
            {hasDietary && (
              <div className="mb-6">
                <h3 className="text-sm font-semibold text-gray-600 uppercase tracking-wide mb-3 flex items-center gap-2">
                  <Utensils className="w-4 h-4" />
                  Dietary
                </h3>
                <div className={`${config.bgColor} rounded-xl p-4 border ${config.borderColor}`}>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(card?.dietary || card?.preferences?.dietary || {}).map(([key, value]) => {
                      if (!value || value === false) return null;
                      return (
                        <Badge key={key} variant="secondary" className={`${config.bgColor} ${config.textColor}`}>
                          {getPreferenceLabel(typeof value === 'boolean' ? key : value)}
                        </Badge>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            {/* Intake Responses */}
            {card?.intake_responses && Object.keys(card.intake_responses).length > 0 && (
              <div className="mb-6">
                <h3 className="text-sm font-semibold text-gray-600 uppercase tracking-wide mb-3">Intake Details</h3>
                <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                  <div className="space-y-2">
                    {Object.entries(card.intake_responses).map(([key, value]) => {
                      if (!value) return null;
                      return (
                        <div key={key}>
                          <p className="text-xs text-gray-500 capitalize">{key.replace(/_/g, ' ')}</p>
                          <p className="font-medium text-sm text-gray-700">{value}</p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            {/* Linked Experiences */}
            {card?.linked_experiences?.length > 0 && (
              <div className="mb-6">
                <h3 className="text-sm font-semibold text-gray-600 uppercase tracking-wide mb-3 flex items-center gap-2">
                  <Link2 className="w-4 h-4" />
                  Linked Experiences
                </h3>
                <div className="space-y-2">
                  {card.linked_experiences.map((linked, idx) => (
                    <div key={idx} className="flex items-center gap-3 bg-gray-50 rounded-lg p-3 border border-gray-100">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                        CATEGORY_CONFIG[linked.category]?.bgColor || 'bg-gray-100'
                      }`}>
                        {(() => {
                          const cfg = CATEGORY_CONFIG[linked.category];
                          if (cfg?.icon) {
                            const LIcon = cfg.icon;
                            return <LIcon className={`w-4 h-4 ${cfg.textColor || 'text-gray-600'}`} />;
                          }
                          return <Sparkles className="w-4 h-4 text-gray-400" />;
                        })()}
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-800">{linked.name}</p>
                        <p className="text-xs text-gray-500">
                          {linked.date 
                            ? new Date(linked.date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }) 
                            : 'Date TBD'}
                          {linked.provider_name ? ` / ${linked.provider_name}` : linked.provider_status === 'diy' ? ' / DIY' : ''}
                        </p>
                      </div>
                      <Badge variant="secondary" className="capitalize text-xs">{linked.category}</Badge>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Note for Provider */}
            {card?.stylist_note && (
              <div className="mb-6">
                <h3 className="text-sm font-semibold text-gray-600 uppercase tracking-wide mb-3 flex items-center gap-2">
                  <MessageSquare className="w-4 h-4" />
                  Note for You
                </h3>
                <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                  <p className="text-gray-700 italic">"{card.stylist_note}"</p>
                </div>
              </div>
            )}

            {/* Phygital Branding Footer */}
            <div className="mt-6 pt-4 border-t border-gray-100 flex items-center justify-between">
              <div className="flex items-center gap-2 text-gray-500">
                <PhygitalIcon className="w-5 h-5 opacity-50" />
                <span className="text-xs">Powered by Phygital</span>
              </div>
              <span className="text-[10px] text-gray-400 font-mono">{shareId}</span>
            </div>
          </div>
        </div>

        {/* Auth-state-aware CTA Block at Bottom */}
        <PublicShareCardCTAs card={card} category={category} categoryName={config.name} CategoryIcon={CategoryIcon} />
      </main>

      {/* Footer */}
      <footer className="py-6 border-t border-gray-100 bg-white mt-8">
        <div className="max-w-2xl mx-auto px-4 flex items-center justify-between">
          <PoweredByPhygital />
          <div className="flex items-center gap-4 text-xs text-gray-500">
            <Link to="/privacy" className="hover:text-gray-700">Privacy</Link>
            <Link to="/terms" className="hover:text-gray-700">Terms</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PublicPreferenceCardPage;
